<template>
  <div class="formulario-modelo">
    <div class="header-flex">
      <div>
        <h1>Mis Procesos de Autoevaluación</h1>
        <p class="subtitulo">Accede a las tareas que te han sido asignadas en cada proceso.</p>
      </div>
      <button class="btn salir" @click="cambiarModo">Cambiar modo</button>
    </div>

    <div class="divider" ></div>

    <!-- Filtros -->
    <div class="filtros-secundarios">
      <div class="filtro-grupo">
        <label class="filtro-label">Estado:</label>
        <select v-model="filtroEstado" class="select-filtro">
          <option value="">Todos</option>
          <option v-for="e in opcionesEstado" :key="e.value" :value="e.value">{{ e.label }}</option>
        </select>
      </div>
      <div class="filtro-grupo">
        <span class="filtro-label">Buscar:</span>
        <input v-model="busqueda" type="text" class="input-busqueda" placeholder="Período..." />
      </div>
      <label class="filtro-toggle" :class="{ activo: ocultarFinalizados }">
        <input type="checkbox" v-model="ocultarFinalizados" :disabled="filtroEstado === 'finalizado'" />
        Ocultar finalizados
      </label>
    </div>

    <div v-if="cargando" class="loading">Cargando tus procesos...</div>

    <div v-else-if="!procesosFiltrados.length" class="mensaje-vacio">
      No tienes procesos asignados en este momento.
    </div>

    <div v-else class="cards-grid">
      <div
        v-for="p in procesosFiltrados"
        :key="p.id"
        :class="['proceso-card', cardAbierta === p.id ? 'card-activa' : '']"
        @click="toggleCard(p.id!)"
      >
        <!-- Encabezado -->
        <div class="card-header-row">
          <h3 class="card-titulo">{{ nombreVersion(p.id_version) }}</h3>
          <span v-if="p.created_at" class="card-creado">{{ formatFecha(p.created_at) }}</span>
        </div>

        <div class="card-meta">
          <span :class="['badge-tipo', p.ambito === 'institucional' ? 'badge-inst' : 'badge-prog']">
            {{ p.ambito === 'institucional' ? 'Institucional' : nombrePrograma(p.id_programa) }}
          </span>
        </div>

        <p v-if="p.observaciones" class="card-obs">{{ p.observaciones }}</p>

        <!-- Progreso del proceso -->
        <div class="progreso-wrapper">
          <div
            v-for="(fase, idx) in fases"
            :key="fase.value"
            :class="['fase-paso', pasoActivo(p.estado, fase.value) ? 'activo' : '', pasoCompletado(p.estado, idx) ? 'completado' : '']"
          >
            <span class="fase-punto"></span>
            <span class="fase-label">{{ fase.label }}</span>
          </div>
        </div>

        <!-- Acciones cuando se abre la card -->
        <transition name="slide-fade">
          <div v-if="cardAbierta === p.id" class="card-acciones">
            <!-- 1. Seguimiento del proceso (solo si el usuario es director del proceso) -->
            <button
              v-if="!esEspecifica(p) && !esProcesoHistorico(p)"
              class="btn salir"
              @click.stop="irAModelo(p.id)"
            >
              Seguimiento
            </button>

            <!-- 2. Asignar tareas (solo programa, según permiso) -->
            <button
              v-if="authStore.tienePermiso('procesos.asignar') && p.ambito === 'programa' && !esProcesoHistorico(p)"
              class="btn guardar"
              :disabled="!puedeAsignarTareas(p)"
              :title="!puedeAsignarTareas(p) ? 'Disponible en fase Ejecución o Evaluación' : ''"
              @click.stop="irAAsignarTareas(p.id)"
            >
              Asignar tareas
            </button>

            <!-- 3. Botón activo según el estado actual del proceso -->
            <button
              v-if="p.estado === 'ejecucion' && !esProcesoHistorico(p)"
              class="btn guardar"
              @click.stop="irAEvidencias(p.id_version, p.id)"
            >
              Subir Evidencias
            </button>
            <button
              v-if="p.estado === 'evaluacion'"
              class="btn guardar"
              @click.stop="irACalificar(p.id_version)"
            >
              Calificar Indicadores
            </button>
            <button
              v-if="authStore.tienePermiso('calificacion.ver') && p.estado === 'informe' && !esProcesoHistorico(p)"
              class="btn guardar"
              @click.stop="irAComparativo(p.id)"
            >
              Comparativo
            </button>
            <button
              v-if="authStore.tienePermiso('calificacion.ver') && p.estado === 'informe'"
              class="btn guardar"
              @click.stop="irAResultados(p.id)"
            >
              Resultados
            </button>
            <button
              v-if="authStore.tienePermiso('informes.ver') && p.estado === 'informe' && !esProcesoHistorico(p)"
              class="btn guardar"
              @click.stop="irAInforme(p.id)"
            >
              Informe
            </button>
            <button
              v-if="authStore.tienePermiso('planes.ver') && !esProcesoHistorico(p)"
              v-show="puedeVerPlan(p) || authStore.esAdmin"
              class="btn guardar"
              :disabled="!puedeVerPlan(p)"
              :title="!puedeVerPlan(p) ? 'Disponible en fase Informe o Finalizado' : ''"
              @click.stop="irAPlanMejoramiento(p.id)"
            >
              Plan de mejoramiento
            </button>

            <button
              v-if="authStore.tienePermiso('informes.ver') && esProcesoHistorico(p) && ['informe', 'finalizado'].includes(p.estado ?? '')"
              class="btn guardar"
              @click.stop="abrirAdjuntarInformes(p)"
            >
              Adjuntar Informes
            </button>

            <button
              v-if="!esEspecifica(p) && p.estado !== 'finalizado' && authStore.tienePermiso('procesos.avanzar')"
              :class="['btn', p.estado === 'informe' ? 'finalizar' : 'avanzar']"
              :disabled="avanzandoId === p.id"
              @click.stop="avanzarProceso(p)"
            >
              {{ avanzandoId === p.id ? 'Procesando…' : labelAvanzar(p.estado) }}
            </button>

          </div>
        </transition>
      </div>
    </div>

    <AdjuntarInformesModal
      :visible="adjuntarVisible"
      :idEjecucion="adjuntarIdEjecucion"
      :periodo="adjuntarPeriodo"
      @cerrar="adjuntarVisible = false"
      @subido="alSubirInformes"
    />

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />
  </div>
</template>

<script lang="ts" setup>
/**
 * Los procesos en los que participa el usuario actual.
 *
 * Filtra por las asignaciones propias (ver `useAsignacionActual`).
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/mis-procesos` (ProcesoAutoevaluacionUsuario) — permiso `procesos.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, onMounted } from 'vue'

const emit = defineEmits(['cambiar-modo'])
import { useRouter, useRoute } from 'vue-router'
import { useGestionProcesos } from '@/store/GestionProcesoAutoevaluacion'
import { useGestionVersiones } from '@/store/GestionVersiones'
import { useGestionProgramas } from '@/store/GestionPrograma'
import { useGestionIndicadorEvaluadores } from '@/store/GestionIndicadorEvaluadores'
import { useGestionIndicadores } from '@/store/GestionIndicadores'
import { useGestionIndicadorInstrumentos } from '@/store/GestionIndicadorInstrumentos'
import useAutenticar from '@/store/IngresoAutenticar'
import type { EstadoProceso, Proceso } from '@/interfaces/ProcesoAutoevaluacion'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import AdjuntarInformesModal from '@/components/AdjuntarInformesModal.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAutenticar()
const procesosStore = useGestionProcesos()
const versionesStore = useGestionVersiones()
const programasStore = useGestionProgramas()
const evaluadoresStore = useGestionIndicadorEvaluadores()
const indicadoresStore = useGestionIndicadores()
const indicadorInstrumentoStore = useGestionIndicadorInstrumentos()

const cargando = ref(false)
const cardAbierta = ref<number | null>(null)
const filtroEstado = ref('')
const busqueda = ref('')
const mensajeVisible = ref(false)
const mensajeTexto = ref('')
const mensajeTipo = ref<'positivo' | 'error'>('positivo')
const avanzandoId = ref<number | null>(null)
const ocultarFinalizados = ref(true)
const adjuntarVisible     = ref(false)
const adjuntarIdEjecucion = ref<number | null>(null)
const adjuntarPeriodo     = ref<string | null>(null)

const fases = [
  { label: 'Preparación', value: 'preparacion' },
  { label: 'Ejecución',   value: 'ejecucion' },
  { label: 'Evaluación',  value: 'evaluacion' },
  { label: 'Informe',     value: 'informe' },
  { label: 'Finalizado',  value: 'finalizado' }
]

const ordenFases: Record<string, number> = {
  preparacion: 0, ejecucion: 1, evaluacion: 2, informe: 3, finalizado: 4
}

const opcionesEstado = [
  { label: 'Preparación', value: 'preparacion' },
  { label: 'Ejecución', value: 'ejecucion' },
  { label: 'Evaluación', value: 'evaluacion' },
  { label: 'Informe', value: 'informe' },
  { label: 'Finalizado', value: 'finalizado' }
]

function calcularPeriodoActual(): string {
  const now = new Date()
  const year = now.getFullYear()
  return (now.getMonth() + 1) <= 6 ? `${year}-1` : `${year}-2`
}

function esProcesoHistorico(p: Proceso): boolean {
  if (p.periodo !== calcularPeriodoActual()) return true
  const idsIndicadores = new Set(
    indicadoresStore.indicadores
      .filter(i => i.id_version === p.id_version && i.id_indicador != null)
      .map(i => i.id_indicador as number)
  )
  if (idsIndicadores.size === 0) return false
  return !indicadorInstrumentoStore.relaciones.some(r => idsIndicadores.has(r.id_indicador))
}

// IDs de procesos donde el usuario tiene cualquier asignación
const ejecucionesDelUsuario = computed(() => {
  const ids = new Set<number>()
  for (const a of evaluadoresStore.misAsignaciones) {
    if (a.id_ejecucion != null) ids.add(a.id_ejecucion)
  }
  return ids
})

// IDs de procesos con asignación explícita de director (id_indicador: null)
const ejecucionesDirector = computed(() => {
  const ids = new Set<number>()
  for (const a of evaluadoresStore.misAsignaciones) {
    if (a.id_indicador == null && a.id_ejecucion != null) ids.add(a.id_ejecucion)
  }
  return ids
})

// true si el usuario solo tiene indicadores específicos asignados, no es director
function esEspecifica(proceso: { id?: number; lideresFactor?: { id_usuario: number }[] }): boolean {
  const idUsuario = authStore.usuarioActual?.id
  if (!idUsuario) return true
  // Administradores ven siempre todo
  if (authStore.esAdmin || authStore.tienePermiso('procesos.crear')) return false
  // El usuario aparece como LiderFactor del proceso → es director
  if (proceso.lideresFactor?.some(lf => lf.id_usuario === idUsuario)) return false
  // Asignación explícita de director (id_indicador: null)
  if (proceso.id != null && ejecucionesDirector.value.has(proceso.id)) return false
  return true
}

const procesosFiltrados = computed(() => {
  let base = procesosStore.procesos.filter(p => p.id != null && ejecucionesDelUsuario.value.has(p.id!))
  if (filtroEstado.value) base = base.filter(p => p.estado === filtroEstado.value)
  if (ocultarFinalizados.value && filtroEstado.value !== 'finalizado') {
    base = base.filter(p => p.estado !== 'finalizado')
  }
  if (busqueda.value.trim()) {
    const t = busqueda.value.trim().toLowerCase()
    base = base.filter(p => p.periodo.toLowerCase().includes(t))
  }
  return base
})

function nombreVersion(idVersion: number): string {
  const v = versionesStore.versiones.find(v => v.id_version === idVersion)
  return v?.nombre ?? `Versión #${idVersion}`
}

function nombrePrograma(idPrograma?: number | null): string {
  if (!idPrograma) return 'Programa'
  const p = programasStore.programas.find(p => p.id_programa === idPrograma)
  return p?.nombre ?? `Programa #${idPrograma}`
}

function formatFecha(fecha: string): string {
  return new Date(fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })
}

function pasoActivo(estadoActual: EstadoProceso | undefined, faseval: string) {
  return estadoActual === faseval
}

function pasoCompletado(estadoActual: EstadoProceso | undefined, idx: number) {
  const actual = ordenFases[estadoActual ?? ''] ?? -1
  return actual > idx
}

// El plan se deriva de la matriz F/D, disponible desde `informe`.
function puedeVerPlan(p: { estado?: EstadoProceso }) {
  return p.estado === 'informe' || p.estado === 'finalizado'
}

function puedeAsignarTareas(p: { estado?: EstadoProceso }) {
  return p.estado === 'ejecucion' || p.estado === 'evaluacion'
}

const siguienteEstado: Record<string, EstadoProceso> = {
  preparacion: 'ejecucion',
  ejecucion:   'evaluacion',
  evaluacion:  'informe',
  informe:     'finalizado',
}

function labelAvanzar(estado?: EstadoProceso): string {
  return estado === 'informe' ? 'Finalizar proceso' : 'Avanzar proceso'
}

async function avanzarProceso(p: Proceso) {
  if (!p.id || !p.estado || p.estado === 'finalizado' || avanzandoId.value !== null) return
  const siguiente = siguienteEstado[p.estado]
  if (!siguiente) return
  avanzandoId.value = p.id
  try {
    const result = await procesosStore.cambiarEstado(p.id, siguiente)
    if (result.ok) {
      mensajeTexto.value = 'Estado del proceso actualizado correctamente.'
      mensajeTipo.value = 'positivo'
    } else {
      mensajeTexto.value = 'No se pudo cambiar el estado del proceso.'
      mensajeTipo.value = 'error'
    }
    mensajeVisible.value = true
  } finally {
    avanzandoId.value = null
  }
}

function cambiarModo() {
  if (route.name === 'ProcesoAutoevaluacionUsuario') {
    router.push({ name: 'ProcesoAutoevaluacion' })
  } else {
    emit('cambiar-modo')
  }
}

function toggleCard(id: number) {
  cardAbierta.value = cardAbierta.value === id ? null : id
}

/**
 * La ejecución viaja en la query porque de ella sale el ÁREA: las fichas de
 * opinión ofrecen los correos ya usados con cada grupo de interés en esa área,
 * y sin la ejecución ese banco queda mudo. Evidencias la propaga a la ficha.
 */
function irAEvidencias(idVersion: number, idEjecucion?: number | null) {
  router.push({
    name: 'EvidenciasInstrumentos',
    params: { id_version: idVersion },
    ...(idEjecucion ? { query: { id_ejecucion: idEjecucion } } : {})
  })
}

function irACalificar(idVersion: number) {
  router.push({ name: 'ListaCalificar', params: { id_version: idVersion } })
}

function irAModelo(idEjecucion?: number) {
  router.push({ name: 'AvanceProceso', params: { id_ejecucion: idEjecucion ?? 0 } })
}

function irAComparativo(id?: number) {
  if (!id) return
  router.push({ name: 'ComparacionCalificaciones', params: { id_ejecucion: id } })
}

function irAResultados(id?: number) {
  if (!id) return
  router.push({ name: 'ResultadosAutoevaluacion', params: { id_ejecucion: id } })
}


function irAInforme(id?: number) {
  if (!id) return
  router.push({ name: 'InformeAutoevaluacion', params: { id_ejecucion: id } })
}

function irAPlanMejoramiento(id?: number) {
  if (!id) return
  router.push({ name: 'PlanMejoramiento', params: { id_ejecucion: id } })
}

function irAAsignarTareas(id?: number) {
  if (!id) return
  router.push({ name: 'AsignarTareasDirector', params: { id_ejecucion: id } })
}

function abrirAdjuntarInformes(p: Proceso) {
  if (!p.id) {
    mensajeTexto.value = 'No se pudo identificar la ejecución.'
    mensajeTipo.value = 'error'
    mensajeVisible.value = true
    return
  }
  adjuntarIdEjecucion.value = p.id
  adjuntarPeriodo.value = p.periodo ?? null
  adjuntarVisible.value = true
}

function alSubirInformes(cantidad: number) {
  mensajeTexto.value = cantidad === 1
    ? 'Archivo adjuntado correctamente.'
    : `${cantidad} archivos adjuntados correctamente.`
  mensajeTipo.value = 'positivo'
  mensajeVisible.value = true
}

onMounted(async () => {
  cargando.value = true
  const idUsuario = authStore.usuarioActual?.id
  await Promise.all([
    procesosStore.cargarTodos(),
    versionesStore.getVersiones(),
    programasStore.cargarTodos(),
    indicadoresStore.cargarTodos(),
    indicadorInstrumentoStore.getRelaciones(),
    idUsuario ? evaluadoresStore.cargarMisAsignaciones(idUsuario) : Promise.resolve()
  ])
  cargando.value = false
})
</script>

<style scoped>
.formulario-modelo {
  background-color: white;
  padding: 2rem;
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
  max-width: 1400px;
  min-width: 500px;
}

h1 { font-size: 1.4rem; font-weight: 700; margin: 0; }

.header-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.subtitulo {
  font-size: 0.9rem;
  color: #666;
  margin: 0;
}

.divider { border: none; border-bottom: 1px solid #e0e0e0; margin: 12px 0 16px; }

/* Filtros */
.filtros-secundarios {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
  align-items: center;
}

.filtro-grupo {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.filtro-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
  white-space: nowrap;
}

.select-filtro {
  padding: 0.35rem 0.8rem;
  border-radius: 16px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
  font-size: 0.85rem;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
}

.select-filtro:focus {
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.input-busqueda {
  padding: 0.35rem 0.8rem;
  border-radius: 16px;
  border: 1px solid #ccc;
  font-size: 0.85rem;
  outline: none;
  transition: border-color 0.15s;
  width: 180px;
}

.input-busqueda:focus {
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

.filtro-toggle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #555;
  cursor: pointer;
  padding: 0.35rem 0.8rem;
  border-radius: 16px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
  transition: border-color 0.15s, background-color 0.15s;
  user-select: none;
}

.filtro-toggle input[type=checkbox] {
  width: auto;
  height: auto;
  margin: 0;
  cursor: pointer;
}

.filtro-toggle.activo {
  border-color: #0e69a0;
  background-color: #eef5fb;
  color: #0e69a0;
}

.filtro-toggle input:disabled ~ span,
.filtro-toggle:has(input:disabled) {
  opacity: 0.45;
  cursor: not-allowed;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 16px;
  padding: 0.5rem 0;
}

.proceso-card {
  background-color: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
  border: 1px solid rgba(0,0,0,0.06);
}

.proceso-card:hover {
  box-shadow: 0 4px 6px rgba(0,0,0,0.07);
  transform: translateY(-2px);
}

.proceso-card.card-activa {
  border-color: #0e69a0;
  border-width: 2px;
  box-shadow: 0 4px 12px rgba(14,105,160,0.15);
}

.card-header-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.6rem;
}

.card-creado {
  font-size: 0.75rem;
  color: #999;
  white-space: nowrap;
  flex-shrink: 0;
}

.card-titulo {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.card-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.periodo {
  font-size: 0.88rem;
  color: #555;
}

.card-obs {
  font-size: 0.83rem;
  color: #777;
  margin: 0 0 0.75rem;
  font-style: italic;
}

.badge-tipo {
  border-radius: 10px;
  padding: 3px 10px;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: capitalize;
}

.badge-inst { background: #e3f2fd; color: #0d47a1; }
.badge-prog { background: #fff3cd; color: #7d4e00; }

.progreso-wrapper {
  display: flex;
  align-items: flex-start;
  gap: 0;
  margin: 1rem 0 0.5rem;
  overflow-x: auto;
}

.fase-paso {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
  position: relative;
  min-width: 60px;
}

.fase-paso::before {
  content: '';
  position: absolute;
  top: 7px;
  left: -50%;
  right: 50%;
  height: 2px;
  background: #e0e0e0;
  z-index: 0;
}

.fase-paso:first-child::before { display: none; }

.fase-paso.completado::before { background: #0e69a0; }

.fase-punto {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #e0e0e0;
  border: 2px solid #e0e0e0;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

.fase-paso.completado .fase-punto {
  background: #0e69a0;
  border-color: #0e69a0;
}

.fase-paso.activo .fase-punto {
  background: #eea222;
  border-color: #eea222;
  box-shadow: 0 0 0 3px rgba(238, 162, 34, 0.25);
}

.fase-label {
  font-size: 0.68rem;
  color: #999;
  margin-top: 4px;
  text-align: center;
  white-space: nowrap;
}

.fase-paso.activo .fase-label { color: #eea222; font-weight: 700; }
.fase-paso.completado .fase-label { color: #0e69a0; }

/* Acciones */
.card-acciones {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;
}

.slide-fade-enter-active { transition: all 0.2s ease; }
.slide-fade-leave-active { transition: all 0.15s ease; }
.slide-fade-enter-from, .slide-fade-leave-to { opacity: 0; transform: translateY(-6px); }

/* Botones */
.btn {
  padding: 0.6rem 1.25rem;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: var(--font-family);
  font-size: 0.9rem;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn.guardar { background-color: #eea222; color: black; }
.btn.guardar:not(:disabled):hover { background-color: #0e69a0; color: white; }
.btn.salir { background-color: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background-color: #0e69a0; color: white; }
.btn.accion { background-color: #0e69a0; color: white; }
.btn.accion:not(:disabled):hover { background-color: #0b5580; color: white; }
.btn.avanzar { background-color: #2e7d32; color: white; }
.btn.avanzar:not(:disabled):hover { background-color: #1b5e20; color: white; }
.btn.finalizar { background-color: #c62828; color: white; }
.btn.finalizar:not(:disabled):hover { background-color: #8e0000; color: white; }

.loading { padding: 1rem; color: #555; }
.mensaje-vacio { padding: 2rem; text-align: center; color: #888; font-size: 0.95rem; }

@media (max-width: 600px) {
  .formulario-modelo { min-width: unset; padding: 1rem; }
  .cards-grid { grid-template-columns: 1fr; }
  .filtros-secundarios { flex-direction: column; align-items: flex-start; }
  .input-busqueda { width: 100%; }
}
</style>
